import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Divider,
  Modal,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { makeStyles } from "@material-ui/styles";
import * as Sentry from "@sentry/browser";
import clsx from "clsx";
import _ from "lodash";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { useAlert } from "react-alert";
import PerfectScrollbar from "react-perfect-scrollbar";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import Navigation from "./Navigation";
import { fetchSources } from "./actions/sources";
import { inDev } from "./config";
import useEnvironment from "./hooks/use-environment";
import useAccountMetadata from "./hooks/useAccountMetadata";
import useFeatureFlags from "./hooks/useFeatureFlag";
import { sources } from "./sources/sources";
import { getFeatureFlag, makeRequest } from "./util";

function jsSnippet(currentEnvironment, accountMetadata) {
  const fpJsPath = "/static/js/freshpaint.js";
  const fpScriptHost = "https://freshpaint-cdn.com";
  let { api_host, app_host } = accountMetadata.config;
  let { ID } = currentEnvironment;
  let apiHost = currentEnvironment.apiHost || api_host;
  let appHost = currentEnvironment.appHost || app_host;
  if (appHost && appHost.indexOf("http") === -1) {
    appHost = `https://${appHost}`;
  }

  let initLine, defaultLibURL, unifiedSnippetURL;
  // default to the environment specific entry script
  unifiedSnippetURL = `https://freshpaint-cdn.com/js/${ID}/freshpaint.js`;
  if (inDev && _.isUndefined(apiHost)) {
    initLine = `freshpaint.init("${ID}", {"api_host": "http://localhost:8084", "app_host": "http://localhost:8080"});`;
    defaultLibURL = `//perfalytics.com${fpJsPath}`;
    // use default script if running in local dev
    unifiedSnippetURL = "https://freshpaint-cdn.com/js/default/freshpaint.js";
  } else if (_.isUndefined(apiHost)) {
    initLine = `freshpaint.init("${ID}");`;
    defaultLibURL = `//perfalytics.com${fpJsPath}`;
  } else {
    initLine = `freshpaint.init("${ID}", {"api_host": "${apiHost}", "app_host": "${appHost}"});`;
    defaultLibURL = `${appHost}${fpJsPath}`;
  }

  let snippet;
  snippet = String.raw`<script type="text/javascript">
    (function(c,a){if(!a.__SV){var b=window;try{var d,m,j,k=b.location,f=k.hash;d=function(a,b){return(m=a.match(RegExp(b+"=([^&]*)")))?m[1]:null};f&&d(f,"fpState")&&(j=JSON.parse(decodeURIComponent(d(f,"fpState"))),"fpeditor"===j.action&&(b.sessionStorage.setItem("_fpcehash",f),history.replaceState(j.desiredHash||"",c.title,k.pathname+k.search)))}catch(n){}var l,h;window.freshpaint=a;a._i=[];a.init=function(b,d,g){function c(b,i){var a=i.split(".");2==a.length&&(b=b[a[0]],i=a[1]);b[i]=function(){b.push([i].concat(Array.prototype.slice.call(arguments,
    0)))}}var e=a;"undefined"!==typeof g?e=a[g]=[]:g="freshpaint";e.people=e.people||[];e.toString=function(b){var a="freshpaint";"freshpaint"!==g&&(a+="."+g);b||(a+=" (stub)");return a};e.people.toString=function(){return e.toString(1)+".people (stub)"};l="disable time_event track track_pageview track_links track_forms track_with_groups add_group set_group remove_group register register_once alias unregister identify name_tag set_config reset opt_in_tracking opt_out_tracking has_opted_in_tracking has_opted_out_tracking clear_opt_in_out_tracking people.set people.set_once people.unset people.increment people.append people.union people.track_charge people.clear_charges people.delete_user people.remove people group page alias ready addEventProperties addInitialEventProperties removeEventProperty addPageviewProperties registerCallConversion".split(" ");
    for(h=0;h<l.length;h++)c(e,l[h]);var f="set set_once union unset remove delete".split(" ");e.get_group=function(){function a(c){b[c]=function(){call2_args=arguments;call2=[c].concat(Array.prototype.slice.call(call2_args,0));e.push([d,call2])}}for(var b={},d=["get_group"].concat(Array.prototype.slice.call(arguments,0)),c=0;c<f.length;c++)a(f[c]);return b};a._i.push([b,d,g])};a.__SV=1.4;b=c.createElement("script");b.type="text/javascript";b.async=!0;b.src="undefined"!==typeof FRESHPAINT_CUSTOM_LIB_URL?
    FRESHPAINT_CUSTOM_LIB_URL:"${defaultLibURL}";(d=c.getElementsByTagName("script")[0])?d.parentNode.insertBefore(b,d):c.head.appendChild(b)}})(document,window.freshpaint||[]);
    ${initLine}
    freshpaint.page();
</script>`;

  if (
    accountMetadata.loaded &&
    getFeatureFlag(accountMetadata, "useUnifiedEntryScript")
  ) {
    snippet = String.raw`<script type="text/javascript">
    (function(){function p(r,e){(e==null||e>r.length)&&(e=r.length);for(var t=0,a=new Array(e);t<e;t++)a[t]=r[t];return a}function v(r){if(Array.isArray(r))return p(r)}function h(r){if(typeof Symbol!="undefined"&&r[Symbol.iterator]!=null||r["@@iterator"]!=null)return Array.from(r)}function A(){throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.")}function I(r,e){if(r){if(typeof r=="string")return p(r,e);var t=Object.prototype.toString.call(r).slice(8,-1);if(t==="Object"&&r.constructor&&(t=r.constructor.name),t==="Map"||t==="Set")return Array.from(t);if(t==="Arguments"||/^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t))return p(r,e)}}function y(r){return v(r)||h(r)||I(r)||A()}var E=function(r,e){if(!e.__SV){try{var t,a,m=window.location,c=m.hash,x=function(n,o){return t=n.match(new RegExp(o+"=([^&]*)")),t?t[1]:null};c&&x(c,"fpState")&&(a=JSON.parse(decodeURIComponent(x(c,"fpState"))),a.action==="fpeditor"&&(window.sessionStorage.setItem("_fpcehash",c),history.replaceState(a.desiredHash||"",r.title,m.pathname+m.search)))}catch(S){}e.__loaded=!1,e.config=!1,e.__SV=2,window.freshpaint=new Proxy(e,{get:function(n,o){return n[o]!==void 0?n[o]:o==="init"?function(l,u,i){var _,d;(_=n)[d="_i"]||(_[d]=[]),n._i.push([l,u||{},i||"freshpaint"])}:function(){for(var l=arguments.length,u=new Array(l),i=0;i<l;i++)u[i]=arguments[i];var _=[o].concat(y(u));return n.push(_),new Proxy(_,{get:function(f,w){return f[w]?f[w]:function(){for(var b=arguments.length,g=new Array(b),s=0;s<b;s++)g[s]=arguments[s];f.length=0,f.push([o].concat(y(u))),f.push([w].concat(y(g)))}}})}}})}};E(document,window.freshpaint||[]);})();
    ${initLine}
    freshpaint.page();
</script>
<script src="${unifiedSnippetURL}"></script>`;
  }

  return snippet;
}

const useTableStyles = makeStyles((theme) => ({
  content: {
    padding: 0,
    "&:last-child": {
      paddingBottom: 0,
    },
  },
  inner: {
    minWidth: 700,
  },
  switchContainerHidden: {
    visibility: "hidden",
  },
  card: {
    width: "65%",
    minWidth: 800,
  },
  clickableImg: {
    maxHeight: 40,
    "&:hover": {
      cursor: "pointer",
    },
  },
  navigation: {
    paddingTop: 24,
  },
  sourceInfo: {
    display: "flex",
    alignItems: "center",
  },
  sourceName: {
    color: "#444",
    fontSize: 16,
    verticalAlign: "center",
    marginLeft: theme.spacing(2),
  },
  tableRow: {
    "&:hover": {
      backgroundColor: "#f0f0f0",
      cursor: "pointer",
      "& button": {
        backgroundColor: "rgba(63, 81, 181, 0.08)",
      },
    },
  },
  tableCell: {
    borderBottom: "none",
    alignItems: "center",
  },
}));

function SourcesPage() {
  const classes = useTableStyles();
  const history = useHistory();
  const alert = useAlert();

  const currentEnvironment = useEnvironment();
  const accountMetadata = useAccountMetadata();
  const flag = useFeatureFlags();
  const [configureModalSource, setConfigureModalSource] = useState(null);
  const dispatch = useDispatch();
  const configuredSources = useSelector((state) => state.sources.data);
  const [sourceToOpen, setSourceToOpen] = useState(null);

  // :TRICKY: When a user wants to configure the source, the behavior depends
  // if they have the SQL Transformations feature flag set. If they don't
  // have it set, we show them a contact us modal. If they do have it set, we
  // redirect them.
  //
  // The tricky part is we may or may not have loaded the feature flag yet.
  // To get around this when a user clicks to configure a source, we keep track
  // of what source they want to open. Then we use a useEffect that runs when
  // the account metadata loads to determine what action to take.
  useEffect(() => {
    if (accountMetadata.loaded) {
      if (
        getFeatureFlag(accountMetadata, "sqlTransformations") &&
        getFeatureFlag(currentEnvironment.project, "sqlTransformations") &&
        sourceToOpen &&
        sourceToOpen.slug
      ) {
        history.push("/sources/" + sourceToOpen.slug);
      } else {
        setConfigureModalSource(sourceToOpen);
      }
    }
  }, [accountMetadata, currentEnvironment, sourceToOpen, history]);

  const handleToggleSource = (source, state) => {
    makeRequest("toggle_source", {
      source_name: source.name,
      state: state,
    })
      .then(() => {
        dispatch(fetchSources());
      })
      .catch((error) => {
        alert.error("Something went wrong. Unable to toggle source.");
        console.log(error);
        Sentry.captureException(error);
      });
  };

  const toggleForSource = (source) => {
    const sourceInfo = configuredSources[source.name];
    return (
      sourceInfo && (
        <Switch
          checked={sourceInfo.enabled}
          color="secondary"
          edge="start"
          title={`${sourceInfo.enabled ? "Disable" : "Enable"} ${source.name}`}
          name="enable-source"
          onChange={() => handleToggleSource(source, !sourceInfo.enabled)}
        />
      )
    );
  };

  return (
    <Navigation className={clsx(classes.navigation)}>
      <Typography component="h2" gutterBottom variant="overline">
        Sources
      </Typography>
      <Typography component="h1" gutterBottom variant="h3">
        Collect data from your website or app
      </Typography>
      <Typography variant="subtitle1">
        New to Freshpaint? Check out our{" "}
        <a
          className="text-brand-red underline"
          href="https://documentation.freshpaint.io/readme/guides/quickstart"
          rel="noopener noreferrer"
          target="_blank"
        >
          Quickstart guide
        </a>{" "}
        to get started.
      </Typography>
      <br />
      <Card className={clsx(classes.card)}>
        <CardHeader title="Sources" />
        <Divider />
        <CardContent className={clsx(classes.content)}>
          <PerfectScrollbar>
            <div className={clsx(classes.inner)}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Integration</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell>Configure</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sources.map((source) => {
                    if (source.key === "CallRail" && !flag("callrailSource")) {
                      return null;
                    }
                    if (source.key === "Invoca" && !flag("invocaSource")) {
                      return null;
                    }

                    const SourceIcon = source.logoSquare;

                    return (
                      <TableRow
                        className={classes.tableRow}
                        key={source.key}
                        onClick={() => setConfigureModalSource(source)}
                      >
                        <TableCell className={classes.tableCell}>
                          <div className={classes.sourceInfo}>
                            {SourceIcon && (
                              <SourceIcon height={42} width={42} />
                            )}
                            <span className={classes.sourceName}>
                              {source.name}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className={classes.tableCell} align="center">
                          {toggleForSource(source)}
                        </TableCell>
                        <TableCell className={classes.tableCell}>
                          <Button
                            variant="outlined"
                            color="primary"
                            onClick={() => setSourceToOpen(source)}
                          >
                            Configure
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </PerfectScrollbar>
        </CardContent>
      </Card>
      <ConfigureSourceModal
        accountMetadata={accountMetadata}
        currentEnvironment={currentEnvironment}
        open={configureModalSource !== null}
        source={configureModalSource}
        onClose={() => setSourceToOpen(null)}
      />
    </Navigation>
  );
}

const useModalStyles = makeStyles((theme) => ({
  root: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    outline: "none",
    boxShadow: theme.shadows[20],
    width: 500,
    maxHeight: "100%",
    overflowY: "auto",
    maxWidth: "100%",
  },
  container: {
    marginTop: theme.spacing(3),
    height: 200,
  },
  actions: {
    justifyContent: "flex-end",
  },
  inputField: {
    marginTop: 16,
    overflowX: "wrap",
    wordBreak: "break-all",
  },
  deactivateButton: {
    backgroundColor: "#B71C1C",
    color: "white",
  },
  closeIconAction: {
    "&:hover": {
      cursor: "pointer",
    },
    margin: 0,
  },
}));

function ConfigureSourceModal({
  source,
  open,
  onClose,
  currentEnvironment,
  accountMetadata,
}) {
  if (source === null) {
    return null;
  } else if (source.modal === "Web") {
    return (
      <WebModal
        open={open}
        onClose={onClose}
        currentEnvironment={currentEnvironment}
        accountMetadata={accountMetadata}
        title="Configure Web"
        body={
          <>
            <p>
              Add the Freshpaint Javascript SDK to your site under the head tag.
              The Autotrack source will automatically start collecting data from
              your site.
            </p>
            {/* <br />
            Not ready to add Freshpaint to your site? You can still test out
            Freshpaint with our{" "}
            <a
              className="text-brand-red underline"
              href="https://documentation.freshpaint.io/faqs/can-i-try-freshpaint-locally-ft.-freshpaint-chrome-extension#1.-install-the-freshpaint-assistant-chrome-extension"
              target="_blank"
              rel="noopener noreferrer"
            >
              Chrome Extension
            </a>
            {"."} */}
          </>
        }
      />
    );
  } else if (source.modal === "Server Side") {
    return (
      <ServerSideModal
        source={source}
        open={open}
        onClose={onClose}
        currentEnvironment={currentEnvironment}
      />
    );
  } else if (source.modal === "React Native") {
    return (
      <ReactNativeModal
        source={source}
        open={open}
        onClose={onClose}
        currentEnvironment={currentEnvironment}
      />
    );
  } else if (source.modal === "Android") {
    return (
      <AndroidModal
        source={source}
        open={open}
        onClose={onClose}
        currentEnvironment={currentEnvironment}
      />
    );
  } else if (source.modal === "iOS") {
    return (
      <IOSModal
        source={source}
        open={open}
        onClose={onClose}
        currentEnvironment={currentEnvironment}
      />
    );
  } else if (source.modal === "Sendinblue") {
    return (
      <SendinblueModal
        source={source}
        open={open}
        onClose={onClose}
        currentEnvironment={currentEnvironment}
      />
    );
  } else if (source.modal === "Mailchimp") {
    return (
      <MailchimpModal
        source={source}
        open={open}
        onClose={onClose}
        currentEnvironment={currentEnvironment}
      />
    );
  } else if (source.modal === "Intercom") {
    return (
      <IntercomModal
        source={source}
        open={open}
        onClose={onClose}
        currentEnvironment={currentEnvironment}
      />
    );
  } else if (source.modal === "SendGrid") {
    return (
      <SendGridModal
        source={source}
        open={open}
        onClose={onClose}
        currentEnvironment={currentEnvironment}
      />
    );
  } else if (source.modal === "Customer.io") {
    return (
      <CustomerIOModal
        source={source}
        open={open}
        onClose={onClose}
        currentEnvironment={currentEnvironment}
      />
    );
  } else if (source.modal === "Kustomer") {
    return (
      <KustomerModal
        source={source}
        open={open}
        onClose={onClose}
        currentEnvironment={currentEnvironment}
      />
    );
  } else if (source.modal === "CallRail") {
    return (
      <CallRailModal
        source={source}
        open={open}
        onClose={onClose}
        currentEnvironment={currentEnvironment}
      />
    );
  } else if (source.modal === "Invoca") {
    return (
      <InvocaModal
        source={source}
        open={open}
        onClose={onClose}
        currentEnvironment={currentEnvironment}
      />
    );
  } else if (source.modal === "Contact Us") {
    return (
      <ContactUsModal
        source={source}
        open={open}
        onClose={onClose}
        project={currentEnvironment.project}
      />
    );
  }

  return null;
}

ConfigureSourceModal.propTypes = {
  source: PropTypes.object,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  currentEnvironment: PropTypes.object.isRequired,
  accountMetadata: PropTypes.object.isRequired,
};

function WebModal({
  open,
  onClose,
  currentEnvironment,
  accountMetadata,
  title,
  body,
}) {
  const alert = useAlert();
  const classes = useModalStyles();

  function handleCopySnippet() {
    const copyText = jsSnippet(currentEnvironment, accountMetadata);
    navigator.clipboard.writeText(copyText);
    alert.success("Copied Code");
  }

  return (
    <Modal
      onClose={onClose}
      open={open}
      // If the visual tagger is on the page, we need to disable the modal's
      // focus-trapping behavior so that you can interact with the visual tagger's
      // overlay even when the modal is open.
      disableEnforceFocus={window._fpEditorLoaded}
    >
      <Card className={clsx(classes.root)}>
        <CardHeader
          title={title}
          classes={{ action: clsx(classes.closeIconAction) }}
          action={<CloseIcon onClick={onClose} />}
        />
        <Divider />
        <CardContent>
          <Typography variant="body1">{body}</Typography>
          <TextField
            fullWidth
            id="freshpaint-javascript-sdk"
            inputProps={{ readOnly: true }}
            multiline={true}
            rows={4}
            label="Freshpaint Javascript SDK"
            name="freshpaint-javascript-sdk"
            value={jsSnippet(currentEnvironment, accountMetadata)}
            variant="outlined"
            className={clsx(classes.inputField)}
          />
        </CardContent>
        <Divider />
        <CardActions className={classes.actions}>
          <Button
            color="primary"
            onClick={() => handleCopySnippet()}
            variant="contained"
          >
            Copy Snippet
          </Button>
        </CardActions>
      </Card>
    </Modal>
  );
}

WebModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  currentEnvironment: PropTypes.object.isRequired,
  title: PropTypes.string.isRequired,
  body: PropTypes.string.isRequired,
};

function ServerSideModal({ open, onClose, currentEnvironment }) {
  const alert = useAlert();
  const classes = useModalStyles();

  function handleCopyEnvID() {
    const copyText = currentEnvironment.ID;
    navigator.clipboard.writeText(copyText);
    alert.success("Copied ID");
  }

  return (
    <Modal onClose={onClose} open={open}>
      <Card className={clsx(classes.root)}>
        <CardHeader
          title="Tracking server-side events"
          classes={{ action: clsx(classes.closeIconAction) }}
          action={<CloseIcon onClick={onClose} />}
        />
        <Divider />
        <CardContent>
          <Typography variant="body1">
            <p>
              Server-side events can be tracked by using our HTTP API with your
              environment ID.
            </p>
            <br />
            <p>
              See the{" "}
              <a
                className="text-brand-red underline"
                href="https://documentation.freshpaint.io/sources/server-side"
                target="_blank"
                rel="noopener noreferrer"
              >
                server-side docs to get started.
              </a>
            </p>
          </Typography>
          <TextField
            fullWidth
            id="freshpaint-env-id"
            inputProps={{ readOnly: true }}
            multiline={true}
            rows={1}
            label="Freshpaint Environment ID"
            name="freshpaint-environment-id"
            value={currentEnvironment.ID}
            variant="outlined"
            className={clsx(classes.inputField)}
          />
        </CardContent>
        <Divider />
        <CardActions className={classes.actions}>
          <Button
            color="primary"
            onClick={() => handleCopyEnvID()}
            variant="contained"
          >
            Copy Environment ID
          </Button>
        </CardActions>
      </Card>
    </Modal>
  );
}

ServerSideModal.propTypes = {
  source: PropTypes.object,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  currentEnvironment: PropTypes.object.isRequired,
};

function ReactNativeModal({ open, onClose, currentEnvironment }) {
  const classes = useModalStyles();

  return (
    <Modal onClose={onClose} open={open}>
      <Card className={clsx(classes.root)}>
        <CardHeader
          title="Track React Native events"
          classes={{ action: clsx(classes.closeIconAction) }}
          action={<CloseIcon onClick={onClose} />}
        />
        <Divider />
        <CardContent>
          <Typography variant="body1">
            You can track React Native events with the Freshpaint React Native
            SDK. The SDK will autocapture touches, edits, and screen views.
          </Typography>
          <br />
          <Typography variant="body1">
            Your environment ID is: <code>{currentEnvironment.ID}</code>
          </Typography>
          <br />
          <Typography variant="body1">
            See the{" "}
            <a
              className="text-brand-red underline"
              href="https://documentation.freshpaint.io/readme/guides/react-native-quickstart-guide"
            >
              React Native quickstart guide
            </a>{" "}
            to get started.
          </Typography>
        </CardContent>
        <Divider />
        <CardActions className={classes.actions}>
          <Button
            color="primary"
            onClick={() =>
              (window.location.href =
                "https://documentation.freshpaint.io/readme/guides/react-native-quickstart-guide")
            }
            variant="contained"
          >
            READ THE QUICKSTART GUIDE
          </Button>
        </CardActions>
      </Card>
    </Modal>
  );
}

ReactNativeModal.propTypes = {
  source: PropTypes.object,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  currentEnvironment: PropTypes.object.isRequired,
};

function AndroidModal({ open, onClose, currentEnvironment }) {
  const classes = useModalStyles();

  return (
    <Modal onClose={onClose} open={open}>
      <Card className={clsx(classes.root)}>
        <CardHeader
          title="Track Android events"
          classes={{ action: clsx(classes.closeIconAction) }}
          action={<CloseIcon onClick={onClose} />}
        />
        <Divider />
        <CardContent>
          <Typography variant="body1">
            You can track Android events with the Freshpaint Android SDK. Your
            environment ID is: <code>{currentEnvironment.ID}</code>
          </Typography>
          <br />
          <Typography variant="body1">
            See the{" "}
            <a
              className="text-brand-red underline"
              href="https://documentation.freshpaint.io/readme/guides/android-quickstart-guide"
            >
              Android quickstart guide
            </a>{" "}
            to get started.
          </Typography>
        </CardContent>
        <Divider />
        <CardActions className={classes.actions}>
          <Button
            color="primary"
            onClick={() =>
              (window.location.href =
                "https://documentation.freshpaint.io/readme/guides/android-quickstart-guide")
            }
            variant="contained"
          >
            READ THE QUICKSTART GUIDE
          </Button>
        </CardActions>
      </Card>
    </Modal>
  );
}

function IOSModal({ open, onClose, currentEnvironment }) {
  const classes = useModalStyles();

  return (
    <Modal onClose={onClose} open={open}>
      <Card className={clsx(classes.root)}>
        <CardHeader
          title="Track iOS events"
          classes={{ action: clsx(classes.closeIconAction) }}
          action={<CloseIcon onClick={onClose} />}
        />
        <Divider />
        <CardContent>
          <Typography variant="body1">
            You can track iOS events with the Freshpaint iOS SDK. Your
            environment ID is: <code>{currentEnvironment.ID}</code>
          </Typography>
          <br />
          <Typography variant="body1">
            See the{" "}
            <a
              className="text-brand-red underline"
              href="https://documentation.freshpaint.io/readme/guides/ios-quickstart-guide"
            >
              iOS quickstart guide
            </a>{" "}
            to get started.
          </Typography>
        </CardContent>
        <Divider />
        <CardActions className={classes.actions}>
          <Button
            color="primary"
            onClick={() =>
              (window.location.href =
                "https://documentation.freshpaint.io/readme/guides/ios-quickstart-guide")
            }
            variant="contained"
          >
            READ THE QUICKSTART GUIDE
          </Button>
        </CardActions>
      </Card>
    </Modal>
  );
}

function SendinblueModal({ open, onClose, currentEnvironment }) {
  const classes = useModalStyles();

  return (
    <Modal onClose={onClose} open={open}>
      <Card className={clsx(classes.root)}>
        <CardHeader
          title="Tracking Sendinblue email activity"
          classes={{ action: clsx(classes.closeIconAction) }}
          action={<CloseIcon onClick={onClose} />}
        />
        <Divider />
        <CardContent>
          <Typography variant="body1">
            Sendinblue email activity can be tracked setting up a Sendinblue
            webhook with the URL for your environment:
          </Typography>
          <TextField
            fullWidth
            id="freshpaint-sendinblue-webhook"
            inputProps={{ readOnly: true }}
            multiline={true}
            rows={2}
            label="Sendinblue webhook URL"
            name="freshpaint-sendinblue-webhook"
            value={`https://api.perfalytics.com/sendinblue/${currentEnvironment.ID}`}
            variant="outlined"
            className={clsx(classes.inputField)}
          />
          <br />
          <Typography variant="body1">
            See the{" "}
            <a
              className="text-brand-red underline"
              href="https://documentation.freshpaint.io/sources/sendinblue"
              target="_blank"
              rel="noopener noreferrer"
            >
              Sendinblue docs to get started.
            </a>
          </Typography>
        </CardContent>
        <Divider />
        <CardActions className={classes.actions}>
          <Button
            color="primary"
            href="https://documentation.freshpaint.io/sources/sendinblue"
            target="_blank"
            rel="noopener noreferrer"
            variant="contained"
          >
            READ THE DOCS
          </Button>
        </CardActions>
      </Card>
    </Modal>
  );
}

function MailchimpModal({ open, onClose, currentEnvironment }) {
  const classes = useModalStyles();

  return (
    <Modal onClose={onClose} open={open}>
      <Card className={clsx(classes.root)}>
        <CardHeader
          title="Tracking Mailchimp email activity"
          classes={{ action: clsx(classes.closeIconAction) }}
          action={<CloseIcon onClick={onClose} />}
        />
        <Divider />
        <CardContent>
          <Typography variant="body1">
            Mailchimp email activity can be tracked setting up a Mailchimp
            webhook with the URL for your environment:
          </Typography>
          <TextField
            fullWidth
            id="freshpaint-mailchimp-webhook"
            inputProps={{ readOnly: true }}
            multiline={true}
            rows={2}
            label="Mailchimp webhook URL"
            name="freshpaint-mailchimp-webhook"
            value={`https://api.perfalytics.com/mailchimp/${currentEnvironment.ID}`}
            variant="outlined"
            className={clsx(classes.inputField)}
          />
          <br />
          <Typography variant="body1">
            See the{" "}
            <a
              className="text-brand-red underline"
              href="https://documentation.freshpaint.io/sources/mailchimp"
              target="_blank"
              rel="noopener noreferrer"
            >
              Mailchimp docs to get started.
            </a>
          </Typography>
        </CardContent>
        <Divider />
        <CardActions className={classes.actions}>
          <Button
            color="primary"
            href="https://documentation.freshpaint.io/sources/mailchimp"
            target="_blank"
            rel="noopener noreferrer"
            variant="contained"
          >
            READ THE DOCS
          </Button>
        </CardActions>
      </Card>
    </Modal>
  );
}

function IntercomModal({ open, onClose, currentEnvironment }) {
  const classes = useModalStyles();

  return (
    <Modal onClose={onClose} open={open}>
      <Card className={clsx(classes.root)}>
        <CardHeader
          title="Tracking Intercom activity"
          classes={{ action: clsx(classes.closeIconAction) }}
          action={<CloseIcon onClick={onClose} />}
        />
        <Divider />
        <CardContent>
          <Typography variant="body1">
            Intercom activity can be tracked setting up an Intercom app with the
            webhook for your environment:
          </Typography>
          <TextField
            fullWidth
            id="freshpaint-intercom-webhook"
            inputProps={{ readOnly: true }}
            multiline={true}
            rows={2}
            label="Intercom webhook URL"
            name="freshpaint-intercom-webhook"
            value={`https://api.perfalytics.com/webhooks/intercom/${currentEnvironment.ID}`}
            variant="outlined"
            className={clsx(classes.inputField)}
          />
          <br />
          <Typography variant="body1">
            See the{" "}
            <a
              className="text-brand-red underline"
              href="https://documentation.freshpaint.io/sources/intercom"
              target="_blank"
              rel="noopener noreferrer"
            >
              Intercom docs to get started.
            </a>
          </Typography>
        </CardContent>
        <Divider />
        <CardActions className={classes.actions}>
          <Button
            color="primary"
            href="https://documentation.freshpaint.io/sources/intercom"
            target="_blank"
            rel="noopener noreferrer"
            variant="contained"
          >
            READ THE DOCS
          </Button>
        </CardActions>
      </Card>
    </Modal>
  );
}

function SendGridModal({ open, onClose, currentEnvironment }) {
  const classes = useModalStyles();

  return (
    <Modal onClose={onClose} open={open}>
      <Card className={clsx(classes.root)}>
        <CardHeader
          title="Tracking SendGrid activity"
          classes={{ action: clsx(classes.closeIconAction) }}
          action={<CloseIcon onClick={onClose} />}
        />
        <Divider />
        <CardContent>
          <Typography variant="body1">
            SendGrid events can be tracked by setting up a webhook with this
            url:
          </Typography>
          <TextField
            fullWidth
            id="freshpaint-sendgrid-webhook"
            inputProps={{ readOnly: true }}
            multiline={true}
            rows={2}
            label="SendGrid webhook URL"
            name="freshpaint-intercom-webhook"
            value={`https://api.perfalytics.com/sendgrid/${currentEnvironment.ID}`}
            variant="outlined"
            className={clsx(classes.inputField)}
          />
          <br />
          <Typography variant="body1">
            See the{" "}
            <a
              className="text-brand-red underline"
              href="https://documentation.freshpaint.io/sources/sendgrid"
              target="_blank"
              rel="noopener noreferrer"
            >
              SendGrid docs to get started.
            </a>
          </Typography>
        </CardContent>
        <Divider />
        <CardActions className={classes.actions}>
          <Button
            color="primary"
            href="https://documentation.freshpaint.io/sources/sendgrid"
            target="_blank"
            rel="noopener noreferrer"
            variant="contained"
          >
            READ THE DOCS
          </Button>
        </CardActions>
      </Card>
    </Modal>
  );
}

function CustomerIOModal({ open, onClose, currentEnvironment }) {
  const classes = useModalStyles();

  return (
    <Modal onClose={onClose} open={open}>
      <Card className={clsx(classes.root)}>
        <CardHeader
          title="Tracking Customer.io activity"
          classes={{ action: clsx(classes.closeIconAction) }}
          action={<CloseIcon onClick={onClose} />}
        />
        <Divider />
        <CardContent>
          <Typography variant="body1">
            Customer.io events can be tracked by setting up the{" "}
            <a
              className="text-brand-red underline"
              target="_blank"
              rel="noopener noreferrer"
              href="https://customer.io/docs/webhooks/#webhook_attempted"
            >
              reporting webhook integration
            </a>{" "}
            with this url:
          </Typography>
          <TextField
            fullWidth
            id="freshpaint-customer-io-webhook"
            inputProps={{ readOnly: true }}
            multiline={true}
            rows={2}
            label="Customer.io webhook URL"
            name="freshpaint-intercom-webhook"
            value={`https://api.perfalytics.com/webhooks/customer-io/${currentEnvironment.ID}`}
            variant="outlined"
            className={clsx(classes.inputField)}
          />
          <br />
          <Typography variant="body1">
            See the{" "}
            <a
              className="text-brand-red underline"
              target="_blank"
              rel="noopener noreferrer"
              href="https://documentation.freshpaint.io/sources/customer.io"
            >
              Customer.io docs to get started.
            </a>
          </Typography>
        </CardContent>
        <Divider />
        <CardActions className={classes.actions}>
          <Button
            color="primary"
            href="https://documentation.freshpaint.io/sources/customer.io"
            target="_blank"
            rel="noopener noreferrer"
            variant="contained"
          >
            READ THE DOCS
          </Button>
        </CardActions>
      </Card>
    </Modal>
  );
}

function KustomerModal({ open, onClose, currentEnvironment }) {
  const classes = useModalStyles();

  return (
    <Modal onClose={onClose} open={open}>
      <Card className={clsx(classes.root)}>
        <CardHeader
          title="Tracking Kustomer activity"
          classes={{ action: clsx(classes.closeIconAction) }}
          action={<CloseIcon onClick={onClose} />}
        />
        <Divider />
        <CardContent>
          <Typography variant="body1">
            Kustomer events can be tracked by setting up the{" "}
            <a
              className="text-brand-red underline"
              target="_blank"
              rel="noopener noreferrer"
              href="https://help.kustomer.com/outbound-webhooks-rkUQvela8"
            >
              outbound webhook
            </a>{" "}
            with this url:
          </Typography>
          <TextField
            fullWidth
            id="freshpaint-kustomer-webhook"
            inputProps={{ readOnly: true }}
            multiline={true}
            rows={2}
            label="Kustomer webhook URL"
            name="freshpaint-kustomer-webhook"
            value={`https://api.perfalytics.com/webhooks/kustomer/${currentEnvironment.ID}`}
            variant="outlined"
            className={clsx(classes.inputField)}
          />
          <br />
          <Typography variant="body1">
            See the{" "}
            <a
              className="text-brand-red underline"
              target="_blank"
              rel="noopener noreferrer"
              href="https://documentation.freshpaint.io/sources/kustomer"
            >
              Kustomer docs to get started.
            </a>
          </Typography>
        </CardContent>
        <Divider />
        <CardActions className={classes.actions}>
          <Button
            color="primary"
            href="https://documentation.freshpaint.io/sources/kustomer"
            target="_blank"
            rel="noopener noreferrer"
            variant="contained"
          >
            READ THE DOCS
          </Button>
        </CardActions>
      </Card>
    </Modal>
  );
}

function CallRailModal({ open, onClose, currentEnvironment }) {
  const classes = useModalStyles();

  function handleCopyEnvID() {
    const copyText = currentEnvironment.ID;
    navigator.clipboard.writeText(copyText);
    alert.success("Copied ID");
  }

  return (
    <Modal onClose={onClose} open={open}>
      <Card className={clsx(classes.root)}>
        <CardHeader
          title="Events from CallRail"
          classes={{ action: clsx(classes.closeIconAction) }}
          action={<CloseIcon onClick={onClose} />}
        />
        <Divider />
        <CardContent>
          <Typography variant="body1">
            Set up the Freshpaint integration in CallRail by copying your
            Freshpaint environment ID
          </Typography>
          <TextField
            fullWidth
            id="freshpaint-env-id"
            inputProps={{ readOnly: true }}
            multiline={true}
            rows={1}
            label="Freshpaint Environment ID"
            name="freshpaint-environment-id"
            value={currentEnvironment.ID}
            variant="outlined"
            className={clsx(classes.inputField)}
          />
        </CardContent>
        <Divider />
        <CardActions className={classes.actions}>
          <Button
            color="primary"
            onClick={() => handleCopyEnvID()}
            variant="contained"
          >
            Copy Environment ID
          </Button>
        </CardActions>
      </Card>
    </Modal>
  );
}

function InvocaModal({ open, onClose, currentEnvironment }) {
  const classes = useModalStyles();

  function handleCopyEnvID() {
    const copyText = currentEnvironment.ID;
    navigator.clipboard.writeText(copyText);
    alert.success("Copied ID");
  }

  return (
    <Modal onClose={onClose} open={open}>
      <Card className={clsx(classes.root)}>
        <CardHeader
          title="Events from Invoca"
          classes={{ action: clsx(classes.closeIconAction) }}
          action={<CloseIcon onClick={onClose} />}
        />
        <Divider />
        <CardContent>
          <Typography variant="body1">
            Set up the Freshpaint integration in Invoca by copying your
            Freshpaint environment ID
          </Typography>
          <TextField
            fullWidth
            id="freshpaint-env-id"
            inputProps={{ readOnly: true }}
            multiline={true}
            rows={1}
            label="Freshpaint Environment ID"
            name="freshpaint-environment-id"
            value={currentEnvironment.ID}
            variant="outlined"
            className={clsx(classes.inputField)}
          />
        </CardContent>
        <Divider />
        <CardActions className={classes.actions}>
          <Button
            color="primary"
            onClick={() => handleCopyEnvID()}
            variant="contained"
          >
            Copy Environment ID
          </Button>
        </CardActions>
      </Card>
    </Modal>
  );
}

ReactNativeModal.propTypes = MailchimpModal.propTypes = CustomerIOModal.propTypes = IntercomModal.propTypes = SendGridModal.propTypes = KustomerModal.propTypes = {
  source: PropTypes.object,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  currentEnvironment: PropTypes.object.isRequired,
};

function ContactUsModal({ source, open, onClose, project }) {
  const classes = useModalStyles();

  return (
    <Modal onClose={onClose} open={open}>
      <Card className={clsx(classes.root)}>
        <CardHeader
          title={`Import ${source.name} data into Freshpaint`}
          classes={{ action: clsx(classes.closeIconAction) }}
          action={<CloseIcon onClick={onClose} />}
        />
        <Divider />
        <CardContent>
          <Typography variant="body1">{source.body}</Typography>
          <br />
          <Typography variant="body1">
            Contact support@freshpaint.io to enable this feature for the{" "}
            {project.name} project. Provide your project ID: {project.ID}.
          </Typography>
        </CardContent>
        <Divider />
        <CardActions className={classes.actions}>
          <Button
            color="primary"
            variant="contained"
            href="https://support.freshpaint.io"
            target="_blank"
            rel="noopener noreferrer"
          >
            Contact Us
          </Button>
        </CardActions>
      </Card>
    </Modal>
  );
}

ContactUsModal.propTypes = {
  source: PropTypes.object,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  project: PropTypes.object.isRequired,
};

export default SourcesPage;
